import AppPanel from '../fw/AppPanel.react';
import React from 'react';
import PropTypes from 'prop-types';
import Relay from 'react-relay';
import {FormattedMessage, injectIntl, defineMessages, IntlShape} from 'react-intl';
import LoadingButton from '../fw/LoadingButton.react.js';
import AcceptInvitationMutation from './AcceptInvitationMutation';
import {wrapNotFound} from '../app/NotFoundHandler.react';
import mutationError from '../channel-defs/mutationError';
import LastOrganisationId from '../app/LastOrganisationId';
import {countBulkInvitationResults} from './bulkInvitationUI.ts';

import './Invitations.scss';
import { User } from '../../common/types/types';

const messages = defineMessages({
    invalidTitle: {id: 'invitationView.invalid.title', 'defaultMessage': 'Invitation is invalid'},
    title: {id: 'invitationView.title', 'defaultMessage': 'Invitation'},
    acceptedTitle: {id: 'invitationView.accepted.title', defaultMessage: 'Invitation accepted'},
    acceptedSummary: {id: 'invitationView.accepted.summary', defaultMessage: 'Added: {added}, already members: {alreadyMember}, failed: {failed}.'},
    continueToOrganisation: {id: 'invitationView.accepted.continue', defaultMessage: 'Continue to organisation'},
    statusAdded: {id: 'invitationView.accepted.statusAdded', defaultMessage: 'added'},
    statusAlreadyMember: {id: 'invitationView.accepted.statusAlreadyMember', defaultMessage: 'already a member'},
    statusFailed: {id: 'invitationView.accepted.statusFailed', defaultMessage: 'failed'},
    acceptanceFailed: {id: 'invitationView.accepted.failed', defaultMessage: 'The invitation group could not be completed. Please open the invitation link again to retry.'},
});

interface InvitationAcceptProps {
    intl: IntlShape;
    secret?: string;
    user: User;
}

class InvitationAccept extends React.Component<InvitationAcceptProps> {

    typeName = 'invitations';

    static propTypes = {
        intl: PropTypes.object.isRequired,
        secret: PropTypes.string,
        user: PropTypes.object.isRequired,
    };

    static contextTypes = {
        router: PropTypes.object
    };

	constructor(props) {
		super(props);

        this.state = {
            acceptanceError: false,
            results: null,
            targetOrganisation: null,
        };
		document.body.classList.add('InvitationAccept');
	}

	componentWillUnmount() {
		document.body.classList.remove('InvitationAccept');
	}

	componentWillMount() {
		if (this.props.user.invitationToAccept && this.props.user.invitationToAccept.length > 0) {
			const data = this.props.user.invitationToAccept[0];
			const secret = this.props.secret;

			let onFailure = (transaction) => {
				mutationError(transaction);
                this.setState({acceptanceError: true, results: []});
			};

			let onSuccess = (response) => {
                const payload = response.acceptInvitation;
                this.setState({
                    acceptanceError: payload.success !== true,
                    results: payload.results || [],
                    targetOrganisation: data.organisation,
                });
			};

			Relay.Store.commitUpdate(new AcceptInvitationMutation({invitation: data, secret: secret}), {
				onFailure,
				onSuccess
			});
		}
	}

	onClickHome() {
		return new Promise(() => {
			this.context.router.replace('/');
		});
	}

    onClickOrganisation() {
        const organisation = this.state.targetOrganisation;
        if (!organisation) {
            return this.onClickHome();
        }
        LastOrganisationId.set(organisation.realId);
        window.location.href = `${window.location.origin}/organisations/${organisation.realId}/`;
        return Promise.resolve();
    }

    formatStatus(status) {
        if (status === 'added') {
            return this.props.intl.formatMessage(messages.statusAdded);
        }
        if (status === 'already_member') {
            return this.props.intl.formatMessage(messages.statusAlreadyMember);
        }
        return this.props.intl.formatMessage(messages.statusFailed);
    }

    render() {
        let { invitationToAccept } = this.props.user;

        if (!invitationToAccept || invitationToAccept.length === 0) {
            return (
                <AppPanel title={this.props.intl.formatMessage(messages.invalidTitle)}>
                    <div className="event-page invalidInvitation">
                        <h2><FormattedMessage defaultMessage="Invitation has already been used (or was invalid, can't tell)." id="acceptInvitation.invalid.description"/></h2>

            <LoadingButton className="btn btn-primary" onClick={() => this.onClickHome()}><FormattedMessage defaultMessage="Go home" id="invitation.goHome" /></LoadingButton>
                    </div>
                </AppPanel>
            );
        }

        if (this.state.results) {
            const counts = countBulkInvitationResults(this.state.results);
            const hasFailure = this.state.acceptanceError || (counts.failed || 0) > 0;
            return (
                <AppPanel title={this.props.intl.formatMessage(messages.acceptedTitle)}>
                    <div className={`alert alert-${hasFailure ? 'warning' : 'success'}`} role="alert">
                        {this.state.acceptanceError
                            ? <p>{this.props.intl.formatMessage(messages.acceptanceFailed)}</p>
                            : null}
                        <p>{this.props.intl.formatMessage(messages.acceptedSummary, {
                            added: counts.added || 0,
                            alreadyMember: counts.already_member || 0,
                            failed: counts.failed || 0,
                        })}</p>
                        <ul>
                            {this.state.results.map((item) => (
                                <li key={item.organisationId}>
                                    {item.organisationName}: {this.formatStatus(item.status)}{item.error ? ` (${item.error})` : ''}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <LoadingButton className="btn btn-primary" onClick={() => this.onClickOrganisation()}>
                        {this.props.intl.formatMessage(messages.continueToOrganisation)}
                    </LoadingButton>
                </AppPanel>
            );
        }

        return null;
    }
}

export default Relay.createContainer(wrapNotFound(injectIntl(InvitationAccept), (props) => {
    return props.user.invitationToAccept;
}), {
    initialVariables: {
        secret: '',
        invitationId: ''
    },
    prepareVariables (variables) {
        return variables;
    },
    fragments: {
        user: () => Relay.QL`
            fragment on User {
                invitationToAccept(id: $invitationId, secret: $secret) {
                    id,
                    organisation {
                        id,
                        realId,
                        name
                    },
                    email,
                    permissions,
                    createdAt,
                }
            }
        `,
    },
});
